import Utils from "@/core/utils/Utils";
import RegionalCommonUtils from "@/core/utils/regional/RegionalCommonUtils";

class RuUtils {
  /**
   * Форматирует российский ИНН с разделителями
   * @param {number|string} inn - ИНН для форматирования (10 или 12 цифр)
   * @returns {string} Отформатированный ИНН (123-456-789 или 123-456-789-01) или пустая строка если некорректный
   */
  static formatINN(inn) {
    const string = Utils.string.toString(inn);
    if (!string) return '';
    
    // Проверяем длину (должна быть 10 или 12)
    if (string.length !== 10 && string.length !== 12) {
      return string;
    }
    
    const digits = string.split('');
    
    if (string.length === 12) {
      // ИНН ИП: 123-456-789-01
      [3, 6, 9].forEach(index => {
        if (digits[index]) {
          digits[index] = `-${digits[index]}`;
        }
      });
    } else {
      // ИНН ЮЛ: 123-456-789
      [3, 6].forEach(index => {
        if (digits[index]) {
          digits[index] = `-${digits[index]}`;
        }
      });
    }
    
    return digits.join('');
  }
  
  static formatBankAccount(account) {
    // Приводим к строке
    const accountStr = Utils.string.toString(account);
    if (!accountStr) return null;
    
    // Проверяем длину российского расчетного счета
    if (accountStr.length !== 20) return null;
    
    // Проверяем что состоит только из цифр
    if (!/^\d{20}$/.test(accountStr)) return null;
    
    // Форматируем по российскому стандарту: АААББ-ВВВ-К-ГГГГ-ДДДДДДД
    return [
      accountStr.slice(0, 5),   // АААББ: юридический статус + цель открытия
      accountStr.slice(5, 8),   // ВВВ: код валюты
      accountStr.slice(8, 9),   // К: контрольная цифра
      accountStr.slice(9, 13),  // ГГГГ: номер подразделения банка
      accountStr.slice(13, 20)  // ДДДДДДД: порядковый номер клиента банка
    ].join('-');
  }
  
  static getBankAccountMask() {
    return '#####-###-#-####-#######';
  }
  
  static getBankIdMask() {
    return '##-##-##-###';
  }
  
  /**
   * Валидация расчётного счёта РФ
   * @param {string|number} account - расчётный счёт для проверки
   * @param {string|number} bik - БИК банка (опционально, для полной валидации)
   * @returns {string|null} - текст ошибки или null если счёт валидный
   */
  static validateBankAccount(account, bik = null) {
    // Приводим к строке
    const accountStr = Utils.string.toString(account);
    
    /*
    if (!accountStr) {
      return 'Расчётный счёт не может быть пустым';
    }
     */
    
    // Счёт должен содержать ровно 20 цифр
    if (accountStr.length !== 20) {
      return 'Расчётный счёт должен содержать 20 цифр';
    }
    
    // Проверяем что состоит только из цифр
    if (!/^\d{20}$/.test(accountStr)) {
      return 'Расчётный счёт должен состоять только из цифр';
    }
    
    // Счёт не может состоять только из нулей
    if (accountStr === '00000000000000000000') {
      return 'Расчётный счёт не может состоять только из нулей';
    }
    
    // Если передан БИК, проверяем контрольную сумму
    if (bik) {
      // Проверяем БИК перед использованием
      const bikError = this.validateRCBIK(bik);
      if (bikError) {
        return `Некорректный БИК: ${bikError}`;
      }
      
      // Проверка контрольной суммы по алгоритму ЦБ РФ
      if (!this.#checkBankAccountControlSum(accountStr, bik)) {
        return 'Неверная контрольная сумма расчётного счёта';
      }
    }
    
    return null;
  }
  
  /**
   * Проверка контрольной суммы расчётного счёта по алгоритму ЦБ РФ
   * @private
   * @param {string} account - расчётный счёт
   * @param {string} bik - БИК банка
   * @returns {boolean} - true если контрольная сумма верна
   */
  static #checkBankAccountControlSum(account, bik) {
    // Берём последние 3 цифры БИК и соединяем с номером счёта
    const bikPart = bik.slice(-3);
    const controlString = bikPart + account;
    
    // Коэффициенты для проверки (веса разрядов)
    const weights = [7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1];
    
    let sum = 0;
    for (let i = 0; i < controlString.length; i++) {
      sum += parseInt(controlString[i]) * weights[i];
    }
    
    // Остаток от деления на 10 должен быть 0
    return sum % 10 === 0;
  }
  
  /**
   * Валидация БИК (Банковский идентификационный код) РФ
   * @param {string|number} bik - БИК для проверки
   * @returns {string|null} - текст ошибки или null если БИК валидный
   */
  static validateRCBIK(bik) {
    // Приводим к строке
    const bikStr = Utils.string.toString(bik);
    
    /*
    if (!bikStr) {
      return 'БИК не может быть пустым';
    }
     */
    
    // БИК должен содержать ровно 9 цифр
    if (bikStr.length !== 9) {
      return 'БИК должен содержать 9 цифр';
    }
    
    // Проверяем что состоит только из цифр
    if (!/^\d{9}$/.test(bikStr)) {
      return 'БИК должен состоять только из цифр';
    }
    
    // Первые 2 цифры - код страны (должен быть 04 для России)
    const countryCode = bikStr.slice(0, 2);
    if (countryCode !== '04') {
      return 'Неверный код страны (должен начинаться с 04)';
    }
    
    // БИК не может состоять только из нулей
    if (bikStr === '000000000') {
      return 'БИК не может состоять только из нулей';
    }
    
    return null;
  }
  
  /**
   * Валидирует российский ИНН
   * @param {string} value - ИНН для валидации
   * @returns {string|null} Ошибка валидации или null если корректный
   */
  static validateINN(value) {
    if (typeof value !== 'string') return null;
    const checkArr = value.split('');
    if (checkArr.length !== 10 && checkArr.length !== 12) {
      return 'ИНН должен быть длиной 10 или 12 символов';
    }
    if (checkArr.length === 10) {
      const number = ((2 * checkArr[0]
            + 4 * checkArr[1]
            + 10 * checkArr[2]
            + 3 * checkArr[3]
            + 5 * checkArr[4]
            + 9 * checkArr[5]
            + 4 * checkArr[6]
            + 6 * checkArr[7]
            + 8 * checkArr[8])
          % 11)
        % 10;
      return number === parseInt(checkArr[9], 10) ? null : 'ИНН неверный';
    }
    
    if (checkArr.length === 12) {
      const numberOne = ((7 * checkArr[0]
            + 2 * checkArr[1]
            + 4 * checkArr[2]
            + 10 * checkArr[3]
            + 3 * checkArr[4]
            + 5 * checkArr[5]
            + 9 * checkArr[6]
            + 4 * checkArr[7]
            + 6 * checkArr[8]
            + 8 * checkArr[9])
          % 11)
        % 10;
      const numberTwo = ((3 * checkArr[0]
            + 7 * checkArr[1]
            + 2 * checkArr[2]
            + 4 * checkArr[3]
            + 10 * checkArr[4]
            + 3 * checkArr[5]
            + 5 * checkArr[6]
            + 9 * checkArr[7]
            + 4 * checkArr[8]
            + 6 * checkArr[9]
            + 8 * checkArr[10])
          % 11)
        % 10;
      return numberOne === parseInt(checkArr[10], 10) && numberTwo === parseInt(checkArr[11], 10)
        ? null
        : 'Некорректный ИНН';
    }
    return null;
  }
  
  /**
   * Валидирует российский ОГРН
   * @param {string} value - ОГРН для валидации
   * @returns {string|null} Ошибка валидации или null если корректный
   */
  static validateOGRN(value) {
    if (typeof value !== 'string') return null;
    if (value.length !== 13) return 'ОГРН должен быть 13 символов длиной';
    return parseInt(value.slice(12, 13), 10)
    === parseInt(`${value.slice(0, -1) % 11}`.slice(-1), 10)
      ? null
      : 'Некорректный ОГРН';
  }
  
  /**
   * Валидирует российский ОГРНИП
   * @param {string} value - ОГРНИП для валидации
   * @returns {string|null} Ошибка валидации или null если корректный
   */
  static validateOGRNIP(value) {
    if (typeof value !== 'string') return null;
    if (value.length !== 15) return 'ОГРНИП должен быть 15 символов длиной';
    return parseInt((parseInt(value.slice(0, -1), 10) % 13).toString()
                                                           .slice(-1), 10)
    === parseInt(value[14], 10)
      ? null
      : 'Некорректный ОГРНИП';
  }
  
  /**
   * Выбирает правильную форму слова для русского языка в зависимости от переданного числительного
   * @param {number} number - Число для которого выбираем форму слова
   * @param {string} oneForm - Форма для единственного числа (1 день)
   * @param {string} fewForm - Форма для 2-4 (2 дня)
   * @param {string} manyForm - Форма для 5+ (5 дней)
   * @returns {string|null} Подходящая форма слова или null если что-то неправильно
   */
  static pluralize(number, oneForm, fewForm, manyForm) {
    return RegionalCommonUtils.pluralize(number, oneForm, fewForm, manyForm, 'RU');
  }
  
  /**
   * Форматирует числовое значение в локализованный десятичный вид с «умным» отсечением нулей в дроби.
   * Прокси на NumberUtils.formatDecimalSmart: сейчас регионы не различаются.
   *
   * Особенности работы:
   * - `minimumFractionDigits: 0` — у целых чисел и незначащих нулей дробная часть не выводится (напр., `120`, а не `120,00`).
   * - `maximumFractionDigits: amount.getScale()` — верхняя граница знаков после запятой берётся из точности самого типа,
   *   а не фиксируется константой (для валют с scale = 2 не будет лишнего третьего знака, а для весовых величин сохранится исходная глубина).
   * - Локаль нормализуется (`_` -> `-`), обеспечивая совместимость стандартов ICU/PHP (`ru_RU`) и BCP 47 (`ru-RU`).
   *
   * @param amount Объект числового значения, реализующий интерфейс Numeric.
   * @param locale Идентификатор локали (по умолчанию 'ru-RU').
   * @returns Отформатированная строка (например, "1 250,5" или "1 250").
   */
  static formatDecimalSmart(amount, locale = 'ru-RU') {
    // региональных различий нет — тело в NumberUtils, здесь ссылка
    return Utils.number.formatDecimalSmart(amount.value, amount.scale, locale);
  }
}

export default RuUtils;